import pluralize from "pluralize";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import axios from "axios";
import { DocumentTextIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
  Bars3BottomLeftIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  RectangleGroupIcon,
  TagIcon,
} from "@heroicons/react/24/solid";
import { ApiRouteHelpers } from "../../../common/root/apiRoutes";
import { Filter } from "../../../common/service/server/dto/FilterDto";
import { SortRule } from "../../../common/service/server/dto/SortRuleDto";
import { DataSubjectRequestSummaryDto } from "../../../common/service/server/dto/DataSubjectRequestDto";
import { DataRequestFilter, RequestTypeFilter } from "../../../common/service/server/Types";
import { NetworkRequest } from "../../../common/models";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import { Fmt } from "../../formatters";
import { fmtRequestTypeState } from "../../formatters/request";
import { useOrganization, usePrimaryOrganizationId, useRequestSummaries } from "../../hooks";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import { RequestInboxTabs } from "./RequestListingPage";
import {
  getCountryRegionFilterOptions,
  matchesCountryRegionFilter,
  getRequestTypeFilterOptions,
  STATE_FILTER_OPTIONS,
} from "./RequestsTable";
import { shouldIncludeOtherLocationOption } from "../../utils/locationUtils";
import "../../components/organisms/consent/ConsentRecordFlatpickr.css";
import { Button, PillTabs, Table } from "@truevault/aquilo";
import { ReportGeneratingModal } from "../../components/organisms/exports/ReportGeneratingModal";
import { TableDensity, TableStyle } from "@truevault/aquilo/src/components/table/Table";
import { RouteHelpers, Routes } from "../../root/routes";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import { useIsFetching } from "@tanstack/react-query";

type FilterType = "all" | "email" | "name" | "type" | "location" | "requestId";

type SearchParams = {
  filterType: FilterType;
  requestType: RequestTypeFilter;
  requestState: string;
  email: string;
  requestPublicId: string;
  firstName: string;
  lastName: string;
  locationCountryRegion: string;
  startDate: Date | null;
  endDate: Date | null;
};

const EMPTY_REQUEST_ROWS: DataSubjectRequestSummaryDto[] = [];

const FILTER_TABS = [
  { key: "all" as FilterType, label: "All Records" },
  { key: "email" as FilterType, label: "Email", Icon: EnvelopeIcon },
  { key: "name" as FilterType, label: "Name", Icon: Bars3BottomLeftIcon },
  { key: "type" as FilterType, label: "Type", Icon: RectangleGroupIcon },
  { key: "requestId" as FilterType, label: "Request ID", Icon: TagIcon },
  { key: "location" as FilterType, label: "Location", Icon: GlobeAltIcon },
];

const getRegionSuffix = (regionCode: string | undefined | null): string => {
  if (!regionCode) return "";
  const trimmed = regionCode.trim();
  if (!trimmed) return "";
  const dashIndex = trimmed.indexOf("-");
  return dashIndex > 0 ? trimmed.slice(dashIndex + 1) : trimmed;
};

const parseLocationSelection = (value: string) => {
  const normalized = value.trim().toUpperCase();
  if (!normalized) {
    return {
      normalized: null as string | null,
      subjectGeoCountry: null as string | null,
      subjectGeoRegion: null as string | null,
    };
  }
  if (normalized.startsWith("US-")) {
    return {
      normalized,
      subjectGeoCountry: "US",
      subjectGeoRegion: getRegionSuffix(normalized),
    };
  }
  return {
    normalized,
    subjectGeoCountry: null,
    subjectGeoRegion: null,
  };
};

const toStartOfDayIso = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
  ).toISOString();
};

const toEndOfDayIso = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
  ).toISOString();
};

const getColumnSetForState = (requestState: string): DataRequestFilter => {
  if (requestState === "closed") {
    return "complete";
  }
  if (requestState === "pending") {
    return "pending";
  }
  return "active";
};

const buildSearchFilters = (params: SearchParams): Filter[] => {
  const filters: Filter[] = [];
  const effectiveEndDate = params.endDate ?? params.startDate;

  if (params.filterType !== "requestId") {
    if (params.startDate) {
      filters.push({ id: "startDate", value: toStartOfDayIso(params.startDate) });
    }
    if (effectiveEndDate) {
      filters.push({ id: "endDate", value: toEndOfDayIso(effectiveEndDate) });
    }
  }

  if (params.filterType === "name") {
    if (params.firstName.trim()) {
      filters.push({ id: "subjectNameFirst", value: params.firstName.trim() });
    }
    if (params.lastName.trim()) {
      filters.push({ id: "subjectNameLast", value: params.lastName.trim() });
    }
  }

  if (params.filterType === "email" && params.email.trim()) {
    filters.push({ id: "subjectEmailAddress", value: params.email.trim() });
  }

  if (params.filterType === "requestId" && params.requestPublicId.trim()) {
    filters.push({ id: "requestPublicId", value: params.requestPublicId.trim().toUpperCase() });
  }

  if (params.filterType === "location") {
    const location = parseLocationSelection(params.locationCountryRegion);
    if (location.normalized) {
      filters.push({ id: "region", value: location.normalized });
      if (location.subjectGeoCountry) {
        filters.push({ id: "subjectGeoCountry", value: location.subjectGeoCountry });
      }
      if (location.subjectGeoRegion) {
        filters.push({ id: "subjectGeoRegion", value: location.subjectGeoRegion });
      }
    }
  }

  if (params.filterType === "type" && params.requestState) {
    filters.push({ id: "state", value: params.requestState });
  }

  return filters;
};

const buildExportFilters = (params: SearchParams): Record<string, string> => {
  const filters: Record<string, string> = {};
  const effectiveEndDate = params.endDate ?? params.startDate;

  if (params.filterType !== "requestId") {
    if (params.startDate) {
      filters.fromDate = toStartOfDayIso(params.startDate);
    }
    if (effectiveEndDate) {
      filters.toDate = toEndOfDayIso(effectiveEndDate);
    }
  }

  if (params.filterType === "email" && params.email.trim()) {
    filters.subjectEmailAddress = params.email.trim();
  }
  if (params.filterType === "requestId" && params.requestPublicId.trim()) {
    filters.requestPublicId = params.requestPublicId.trim().toUpperCase();
  }

  if (params.filterType === "name") {
    if (params.firstName.trim()) {
      filters.subjectNameFirst = params.firstName.trim();
    }
    if (params.lastName.trim()) {
      filters.subjectNameLast = params.lastName.trim();
    }
  }

  if (params.filterType === "type" && params.requestType !== "all") {
    filters.requestType = params.requestType;
  }

  if (params.filterType === "type" && params.requestState) {
    filters.requestState = params.requestState;
  }

  if (params.filterType === "location") {
    const location = parseLocationSelection(params.locationCountryRegion);
    if (location.normalized) {
      filters.region = location.normalized;
      if (location.subjectGeoCountry) {
        filters.subjectGeoCountry = location.subjectGeoCountry;
      }
      if (location.subjectGeoRegion) {
        filters.subjectGeoRegion = location.subjectGeoRegion;
      }
    }
  }

  return filters;
};

const canSearch = (
  filterType: FilterType,
  email: string,
  requestPublicId: string,
  firstName: string,
  lastName: string,
  locationCountryRegion: string,
  requestType: string,
  requestState: string,
): boolean => {
  if (filterType === "email") {
    return email.trim().length > 0;
  }
  if (filterType === "requestId") {
    return requestPublicId.trim().length > 0;
  }
  if (filterType === "name") {
    return firstName.trim().length > 0 || lastName.trim().length > 0;
  }
  if (filterType === "location") {
    return locationCountryRegion.trim().length > 0;
  }
  if (filterType === "type") {
    return requestType !== "all" || requestState.length > 0;
  }
  return true;
};

export function RecordLookupContent() {
  const orgId = usePrimaryOrganizationId();
  const [org] = useOrganization(orgId);

  const [filterType, setFilterType] = useState<FilterType>("all");
  const [email, setEmail] = useState("");
  const [requestPublicId, setRequestPublicId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [locationCountryRegion, setLocationCountryRegion] = useState("");
  const [requestType, setRequestType] = useState<RequestTypeFilter>("all");
  const [requestState, setRequestState] = useState("");

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [maxSelectableDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });

  const [hasSearched, setHasSearched] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    filterType: "all",
    requestType: "all",
    requestState: "",
    email: "",
    requestPublicId: "",
    firstName: "",
    lastName: "",
    locationCountryRegion: "",
    startDate: null,
    endDate: null,
  });

  const [pageParam, setPageParam] = useState(1);
  const [sortParam] = useState<SortRule[]>([{ id: "submitted", desc: true }]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const history = useHistory();
  const [privacyCenter] = usePrivacyCenter(orgId);
  const includeOtherOption = shouldIncludeOtherLocationOption(privacyCenter);

  const requestTypeOptions = org ? getRequestTypeFilterOptions(org.featureGdpr) : [];
  const locationCountryRegionOptions = useMemo(
    () =>
      getCountryRegionFilterOptions(
        org ?? null,
        privacyCenter?.regionRequestTypeMap,
        includeOtherOption,
      ),
    [includeOtherOption, org, privacyCenter?.regionRequestTypeMap],
  );
  const searchFilters = useMemo(() => buildSearchFilters(searchParams), [searchParams]);
  const searchType = searchParams.filterType === "type" ? searchParams.requestType : "all";
  const tableColumnSet =
    searchParams.filterType === "type" && searchParams.requestState
      ? getColumnSetForState(searchParams.requestState)
      : "active";
  const isAllRecordsMode = filterType === "all";
  const showDateRangeFilter = filterType !== "requestId";
  const hasCompleteDateRange = Boolean(startDate && endDate);

  const [dataRequestPage, _requestsRequest] = useRequestSummaries(
    orgId,
    undefined,
    searchFilters,
    sortParam,
    undefined,
    searchType,
    Math.max(pageParam - 1, 0),
  );

  const rawTableRows = dataRequestPage?.data ?? EMPTY_REQUEST_ROWS;
  const tableRows = useMemo(() => {
    if (searchParams.filterType !== "location" || !searchParams.locationCountryRegion) {
      return rawTableRows;
    }
    return rawTableRows.filter((row) =>
      matchesCountryRegionFilter(row, searchParams.locationCountryRegion),
    );
  }, [rawTableRows, searchParams.filterType, searchParams.locationCountryRegion]);

  const resultCount = tableRows.length;
  const dataRequestsFetchCount = useIsFetching({ queryKey: ["dataRequests", orgId] });
  const isSearching = isSearchTriggered && dataRequestsFetchCount > 0;
  const resultCountLabel = pluralize("Result", resultCount, true);
  const dateColumnLabel = tableColumnSet === "complete" ? "Closed" : "Submitted";
  const totalPages = dataRequestPage?.meta?.totalPages ?? 0;

  useEffect(() => {
    if (isSearchTriggered && dataRequestsFetchCount === 0) {
      setIsSearchTriggered(false);
    }
  }, [isSearchTriggered, dataRequestsFetchCount]);

  const handleSearch = () => {
    setPageParam(1);
    setHasSearched(true);
    setIsSearchTriggered(true);
    setSearchParams({
      filterType,
      requestType,
      requestState,
      email,
      requestPublicId,
      firstName,
      lastName,
      locationCountryRegion,
      startDate,
      endDate,
    });
  };
  const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isAllRecordsMode || isGeneratingReport) {
      return;
    }
    if (
      !canSearch(
        filterType,
        email,
        requestPublicId,
        firstName,
        lastName,
        locationCountryRegion,
        requestType,
        requestState,
      )
    ) {
      return;
    }
    handleSearch();
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      await axios.post(ApiRouteHelpers.organizationExports(orgId), {
        exportType: "RECORD_LOOKUP",
        filters: buildExportFilters(searchParams),
      });
      setShowGenerationModal(true);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleGenerateAllRecordsReport = async () => {
    if (!startDate || !endDate) {
      return;
    }
    setIsGeneratingReport(true);
    setHasSearched(false);
    try {
      await axios.post(ApiRouteHelpers.organizationExports(orgId), {
        exportType: "RECORD_LOOKUP",
        filters: buildExportFilters({
          filterType: "all",
          requestType: "all",
          requestState: "",
          email: "",
          requestPublicId: "",
          firstName: "",
          lastName: "",
          locationCountryRegion: "",
          startDate,
          endDate,
        }),
      });
      setShowGenerationModal(true);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="tw-boundary">
      {showGenerationModal && (
        <ReportGeneratingModal
          reportName="Record Lookup"
          onClose={() => setShowGenerationModal(false)}
        />
      )}

      <div className="mb-6 rounded-lg border border-[#5331C6] bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-neutral-800 mb-1">Requests Records Lookup</h2>
        <p className="text-sm text-neutral-500 mb-4">
          Search request records and generate downloadable csv reports.
        </p>

        <PillTabs
          label="Filter by"
          tabs={FILTER_TABS.map((tab) => ({
            id: tab.key,
            label: tab.label,
            selected: filterType === tab.key,
            onClick: () => setFilterType(tab.key),
            icon: tab.Icon ? <tab.Icon className="h-5 w-5" /> : undefined,
          }))}
        />

        <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={handleFilterSubmit}>
          {filterType === "email" && (
            <div className="flex-1" style={{ maxWidth: "320px" }}>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Consumer Email
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Search email text"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
          )}

          {filterType === "requestId" && (
            <div className="flex-1" style={{ maxWidth: "320px" }}>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Request ID</label>
              <input
                type="text"
                value={requestPublicId}
                onChange={(e) => setRequestPublicId(e.target.value)}
                placeholder="EKQA5YU2C"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
          )}

          {filterType === "name" && (
            <>
              <div style={{ width: "220px" }}>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Consumer First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <div style={{ width: "220px" }}>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Consumer Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
            </>
          )}

          {filterType === "type" && (
            <>
              <div style={{ width: "240px" }}>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Request Type
                </label>
                <select
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value as RequestTypeFilter)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                >
                  <option value="all">Select a type…</option>
                  {requestTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ width: "240px" }}>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Request Status
                </label>
                <select
                  value={requestState}
                  onChange={(e) => setRequestState(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                >
                  <option value="">Optional status…</option>
                  {STATE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {filterType === "location" && (
            <div style={{ width: "280px" }}>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Country & Region
              </label>
              <select
                value={locationCountryRegion}
                onChange={(e) => setLocationCountryRegion(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                <option value="">Select country/region</option>
                {locationCountryRegionOptions.map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showDateRangeFilter && (
            <div className="shrink-0">
              <label className="mb-1 block text-sm font-medium text-neutral-700">
                Date Range <span className="font-normal text-neutral-500">(Optional)</span>
              </label>
              <div className="relative min-w-[325px]">
                <Flatpickr
                  className="w-full rounded-md border border-neutral-300 bg-white py-[9px] pl-[13px] pr-10 text-sm text-neutral-900 shadow-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600"
                  value={startDate && endDate ? [startDate, endDate] : startDate ? [startDate] : []}
                  options={{
                    mode: "range",
                    maxDate: maxSelectableDate,
                    dateFormat: "M j, Y",
                  }}
                  placeholder="Select date range"
                  onChange={(dates: Date[]) => {
                    if (!dates.length) {
                      setStartDate(null);
                      setEndDate(null);
                      return;
                    }
                    setStartDate(dates[0]);
                    setEndDate(dates.length > 1 ? dates[1] : null);
                  }}
                />
              </div>
            </div>
          )}

          {isAllRecordsMode ? (
            <Button
              type="button"
              size="s"
              color="secondary"
              onClick={handleGenerateAllRecordsReport}
              disabled={isGeneratingReport || !hasCompleteDateRange}
              customSizeClassname="h-8.5 py-[9px] text-sm leading-[20px]"
            >
              <span className="inline-flex items-center gap-2 whitespace-nowrap">
                <DocumentTextIcon className="h-5 w-5" />
                {isGeneratingReport ? "Generating..." : "Generate Report"}
              </span>
            </Button>
          ) : (
            <Button
              type="submit"
              size="s"
              color="secondary"
              onClick={() => undefined}
              disabled={
                isGeneratingReport ||
                !canSearch(
                  filterType,
                  email,
                  requestPublicId,
                  firstName,
                  lastName,
                  locationCountryRegion,
                  requestType,
                  requestState,
                )
              }
              customSizeClassname="h-8.5 py-[9px] text-sm leading-[20px]"
            >
              <span className="inline-flex items-center gap-2 whitespace-nowrap">
                <MagnifyingGlassIcon className="h-5 w-5" />
                Search
              </span>
            </Button>
          )}
        </form>
      </div>

      <div className="mt-2 mb-4 rounded-md border border-blue-600 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        Your generated reports are available for download at{" "}
        <a className="font-medium underline" href={Routes.documents.recordExports}>
          Privacy Docs → Record Exports
        </a>
        .
      </div>

      {hasSearched && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[20px] font-medium leading-[30px] text-neutral-900">Results</h2>
            {resultCount > 0 && (
              <Button
                type="button"
                size="s"
                color="secondary"
                onClick={handleGenerateReport}
                disabled={isGeneratingReport}
                customSizeClassname="h-8.5 py-[9px] text-sm leading-[20px]"
              >
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  <DocumentTextIcon className="h-5 w-5" />
                  {isGeneratingReport ? "Generating..." : "Download CSV"}
                </span>
              </Button>
            )}
          </div>
          <p className="mb-4 text-sm text-neutral-600">Found {resultCountLabel}</p>

          {isSearching ? (
            <div className="tw-boundary">
              <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[8px] border border-neutral-200 bg-white px-6 py-8">
                <CircularProgress size={28} />
                <p className="mt-3 text-sm text-neutral-600">Loading results…</p>
              </div>
            </div>
          ) : (
            <div className="tw-boundary">
              <Table<DataSubjectRequestSummaryDto>
                data={tableRows}
                keyExtractor={(item) => item.id}
                tableStyle={TableStyle.OUTLINED}
                tableDensity={TableDensity.COMPACT}
                columns={[
                  {
                    key: "id",
                    header: "Request ID",
                    render: (item) => item.id,
                  },
                  {
                    key: "state",
                    header: "Status",
                    render: (item) => fmtRequestTypeState(item.state),
                  },
                  {
                    key: "subjectEmailAddress",
                    header: "Requester Email",
                    render: (item) => item.subjectEmailAddress || "—",
                  },
                  {
                    key: "subjectFirstName",
                    header: "First",
                    render: (item) => item.subjectFirstName || "—",
                  },
                  {
                    key: "subjectLastName",
                    header: "Last",
                    render: (item) => item.subjectLastName || "—",
                  },
                  {
                    key: "location",
                    header: "Country & Region",
                    render: (item) => {
                      const region = item.dataRequestType?.regulationLaw?.region;
                      if (!region) return "—";
                      if (region.regionGroup === "EEA_UK") return "EEA/UK";
                      if (region.regionGroup === "CANADA") return "Canada";
                      if (region.regionGroup === "UNITED_STATES") {
                        return `US - ${region.regionName}`;
                      }
                      if (region.regionGroup === "OTHER") return "Other";
                      return region.regionName || "—";
                    },
                  },
                  {
                    key: "requestType",
                    header: "Type",
                    render: (item) => item.dataRequestType?.shortLabel || "Unknown",
                  },
                  {
                    key: "requestDate",
                    header: dateColumnLabel,
                    render: (item) =>
                      Fmt.Date.fmtDate(
                        tableColumnSet === "complete" ? item.closedAt : item.requestDate,
                      ) || "—",
                  },
                ]}
                actionsLabel="Actions"
                actions={[
                  {
                    label: "View",
                    onClick: (item) => {
                      history.push(RouteHelpers.requests.detail(item.id), {
                        fromRecordLookup: true,
                      });
                    },
                  },
                ]}
                zeroState={<p className="text-sm text-neutral-500">No matching records found.</p>}
              />
              {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                  <Pagination
                    count={totalPages}
                    page={pageParam}
                    onChange={(_, nextPage) => setPageParam(nextPage)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RecordLookupPage() {
  const [_org, orgRequest] = useOrganization(usePrimaryOrganizationId());
  const currentOrgUser = useSelfOrganizationUser();
  const [everRendered, setEverRendered] = useState(false);

  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest)) {
      setEverRendered(true);
    }
  }, [orgRequest]);

  if (!everRendered || !currentOrgUser) {
    return <LoadingPageWrapper />;
  }

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Record Lookup" />
      <div style={{ maxWidth: "1214px" }}>
        <div className="tw-boundary">
          <RequestInboxTabs selectedTabKey="record-lookup" />
        </div>
        <div className="mt-lg" data-edit-mode-read-only-allow>
          <RecordLookupContent />
        </div>
      </div>
    </StandardPageWrapper>
  );
}
